import { cn } from "@/lib/utils";
import { MinusIcon, PlusIcon } from "lucide-react";
import type { ReactNode } from "react";
import React from "react";
import { ClickableIcon } from "./input";

interface SectionHeaderProps {
  title: string;
  isExpanded: boolean;
  onToggle: () => void;
  isActive?: boolean;
  icon?: ReactNode;
  children?: ReactNode;
  titleClassName?: string;
  rightIconOverride?: ReactNode;
  "data-testid"?: string;
}

export const SectionHeader = React.memo<SectionHeaderProps>(
  ({
    title,
    isExpanded,
    onToggle,
    isActive = true,
    icon,
    children,
    titleClassName,
    rightIconOverride,
    "data-testid": dataTestId,
  }) => {
    return (
      <>
        <button
          type="button"
          data-testid={dataTestId}
          className="w-full flex flex-row justify-between items-center cursor-pointer rounded transition-colors px-4 py-3.5 group"
          onClick={onToggle}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onToggle();
            }
          }}
          tabIndex={0}
          aria-expanded={isExpanded}
          aria-controls={`${title
            .toLowerCase()
            .replace(/\s+/g, "-")}-section-content`}
        >
          <div
            className={cn(
              "flex items-center gap-2 text-xs font-medium",
              !isActive && "text-muted-foreground",
              titleClassName,
            )}
          >
            {icon}
            {title}
          </div>
          {rightIconOverride ?? (
            <ClickableIcon className="flex flex-col items-center justify-center w-4 h-4 group-hover:text-icons-primary text-icons-secondary transition-colors">
              {isExpanded ? <MinusIcon /> : <PlusIcon />}
            </ClickableIcon>
          )}
        </button>
        {children && isExpanded && <div className="m-4 mt-2">{children}</div>}
      </>
    );
  },
);
